import { useEffect, useState } from "react";

import { APICallStatus } from "src/contentful";

import type { Request as TesseractRequest } from "@atlassian/tesseract-runtime-globals";

import {
    BxpExpressServiceSupportedRegions,
    TesseractRuntimeSupportedRegions
} from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

export type UserLocale = string;
export const DEFAULT_COUNTRY_CODE: UserLocale = "US";
export const LOCALE_CACHE_KEY = "bxp.currentLocale";

export interface LocationData {
    countryIsoCode: string;
    regionIsoCode: string;
}

export type CurrencyISOCode = "USD" | "JPY";
export const COUNTRY_CODE_TO_CURRENCY: Record<UserLocale, CurrencyISOCode> = {
    US: "USD",
    JP: "JPY",
    ja: "JPY"
};

export const getCurrencyFromUserLocale = (
    userLocale: UserLocale
): CurrencyISOCode => {
    // @ts-ignore
    return (
        COUNTRY_CODE_TO_CURRENCY[userLocale] ??
        COUNTRY_CODE_TO_CURRENCY[DEFAULT_COUNTRY_CODE]
    );
};

export async function fetchUserLocaleS2SWithLocationData(
    baseUrl: string,
    request: Request | TesseractRequest
): Promise<LocationData> {
    if (typeof window === "undefined") {
        // @ts-ignore
        const environmentType: string = globalThis.ssrContext?.environmentType;
        const modeEnv = process.env["MODE"];
        const version = process.env["VERSION"];
        const isCI = process.env["CI"] === "true";
        const nodeEnv = process.env["NODE_ENV"];

        console.log("environmentType", environmentType);
        console.log("modeEnv", modeEnv);
        console.log("nodeEnv", nodeEnv);
        console.log("version", version);

        const isLocalOrCI = version === "local" || isCI;
        // Skip location API call in local and CI mode to avoid network requests during local development
        // Not skipping in test since we use spys instead of mocks
        if (isLocalOrCI && nodeEnv !== "test") {
            console.log(
                "[LOCATION API] Local dev mode detected - Skipping /endpoint/location call",
                {
                    environmentType,
                    MODE: modeEnv,
                    returning: {
                        countryIsoCode: DEFAULT_COUNTRY_CODE,
                        regionIsoCode: ""
                    }
                }
            );
            return {
                countryIsoCode: DEFAULT_COUNTRY_CODE,
                regionIsoCode: ""
            };
        }

        console.log(
            "[LOCATION API] Not in dev mode - Will make /endpoint/location call",
            { environmentType, MODE: modeEnv, VERSION: version, CI: isCI }
        );

        const headers: Record<string, string> = {};

        //@ts-ignore
        const awsRegion: string = globalThis.ssrContext
            .awsRegion as TesseractRuntimeSupportedRegions;

        // S2S calls only work if the downstream service exists in the same region that our tesseract runtime is in
        const downStreamServiceExistsInSameTesseractRuntimeRegion: boolean =
            BxpExpressServiceSupportedRegions.has(awsRegion);

        if (!downStreamServiceExistsInSameTesseractRuntimeRegion) {
            console.log(
                "Downstream service does not exist in the same region as current tesseract region. Current tesseract region: " +
                    awsRegion
            );
            const fallbackLocale = await fetchUserLocale(baseUrl);
            return {
                countryIsoCode: fallbackLocale,
                regionIsoCode: ""
            };
        }

        headers["x-ssr-service-name"] = "bxp-express";
        // bxp-express service does not have any dev instances so when we're in dev, point to staging
        // Note: This code path should not be reached in dev mode due to early return above
        if (environmentType === "dev") {
            headers["x-ssr-service-env"] = "staging";
            headers["x-ssr-service-region"] = "us-east-1"; // Bxp-express service in staging only lives in us-east-1 or us-west-2
        }

        //@ts-ignore
        headers["X-Forwarded-For"] = request?.headers["x-forwarded-for"];

        console.log(
            "[LOCATION API] Making fetch call to /location (routes to /endpoint/location)",
            {
                url: "/location",
                headers: Object.keys(headers)
            }
        );

        return await fetch(`/location`, { headers: headers })
            .then((response) => {
                return response.json();
            })
            .then((json) => {
                return {
                    countryIsoCode:
                        (json.countryIsoCode as UserLocale) ||
                        DEFAULT_COUNTRY_CODE,
                    regionIsoCode: json.regionIsoCode || ""
                };
            })
            .catch(async () => {
                // If there's an error with s2s call - fall back to original fetchUserLocale
                const fallbackLocale = await fetchUserLocale(baseUrl);
                return {
                    countryIsoCode: fallbackLocale,
                    regionIsoCode: ""
                };
            });
    } else {
        const fallbackLocale = await fetchUserLocale(baseUrl);
        return {
            countryIsoCode: fallbackLocale,
            regionIsoCode: ""
        };
    }
}

export async function fetchUserLocaleS2S(
    baseUrl: string,
    request: Request | TesseractRequest
): Promise<UserLocale> {
    let localeFromStorage;
    if (typeof window !== "undefined") {
        localeFromStorage = sessionStorage.getItem(
            LOCALE_CACHE_KEY
        ) as UserLocale;
    }

    if (localeFromStorage) {
        return Promise.resolve(localeFromStorage);
    }

    const locationData = await fetchUserLocaleS2SWithLocationData(
        baseUrl,
        request
    );
    return locationData.countryIsoCode;
}

export async function fetchUserLocale(baseUrl: string): Promise<UserLocale> {
    let localeFromStorage;
    if (typeof window !== "undefined") {
        localeFromStorage = sessionStorage.getItem(
            LOCALE_CACHE_KEY
        ) as UserLocale;
    }

    if (localeFromStorage) {
        return Promise.resolve(localeFromStorage);
    }

    const locationData = await fetchLocationDataWithBaseUrl(baseUrl);
    return locationData.countryIsoCode;
}

export async function fetchLocationDataWithBaseUrl(
    baseUrl: string
): Promise<LocationData> {
    return await fetch(`${baseUrl}/location`)
        .then((response) => {
            return response.json();
        })
        .then((json) => {
            return {
                countryIsoCode:
                    (json.countryIsoCode as UserLocale) || DEFAULT_COUNTRY_CODE,
                regionIsoCode: json.regionIsoCode || ""
            };
        })
        .catch(() => {
            return {
                countryIsoCode: DEFAULT_COUNTRY_CODE,
                regionIsoCode: ""
            };
        });
}

export function useGeoLocation(baseUrl: string) {
    const [status, setStatus] = useState<APICallStatus>("idle");
    const [countryCode, setCountryCode] = useState<UserLocale>();

    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const locale = await fetchUserLocale(baseUrl);
            setStatus("complete");
            setCountryCode(locale);
        };
        fetchData();
    }, []);
    return { status, countryCode };
}
